import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useClient } from '../context/ClientContext';
import ClientWorkspaceLayout from '../components/workspace/ClientWorkspaceLayout';
import {
  MessageSquare, X, RefreshCw, WifiOff, Flame, ChevronDown
} from 'lucide-react';
import {
  fetchClientTimeline,
  fetchClientDailyTargets,
} from '../services/clientApi';
import {
  TIMELINE_PAGE_SIZE,
  normalizeTimelineEvents,
  mergeTimelineEvents,
  oldestCursor,
  sourceErrorsFromPayload,
  anySourceError,
  allSourcesFailed,
  computeHasMore,
} from '../utils/timelineNormalize';
import ChatBubble from '../components/terminal/ChatBubble';
import FoodLogCard from '../components/terminal/FoodLogCard';
import WeightLogCard from '../components/terminal/WeightLogCard';
import FoodLogDrawer from '../components/terminal/FoodLogDrawer';
import TimelineSkeleton from '../components/terminal/TimelineSkeleton';

function useOnlineStatus() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function ChatPage() {
  const { isDarkMode, themeClasses: baseThemeClasses } = useTheme();
  const { selectedClient } = useClient();
  const isOnline = useOnlineStatus();
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';
  const he = language === 'hebrew';

  const themeClasses = {
    ...baseThemeClasses,
    inputField: `w-full rounded-md border px-4 py-2.5 text-[13px] outline-none transition-colors duration-[120ms] ease-out focus:border-forest-ondark focus:ring-2 focus:ring-forest-ondark/20 ${isDarkMode ? 'bg-surface-dark border-line-dark text-ink-dark placeholder-ink-muted-dark' : 'bg-surface border-line text-ink placeholder-ink-muted'}`,
    btnPrimary: `min-h-[44px] px-5 py-2.5 rounded-md font-semibold bg-forest-ondark text-white dark:text-canvas-dark hover:opacity-90 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark focus-visible:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none transition-opacity duration-[120ms] ease-out`,
    btnSecondary: `min-h-[44px] px-5 py-2.5 rounded-md font-medium border border-line dark:border-line-dark bg-transparent text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-ondark focus-visible:ring-offset-2 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-[120ms] ease-out`,
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    document.documentElement.dir = direction;
    document.documentElement.lang = language === 'hebrew' ? 'he' : 'en';
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, [direction, language]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const apply = () => {
      if (mq.matches) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } else {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sourceErrors, setSourceErrors] = useState({ messages: null, food_logs: null, weight_logs: null });
  const [clientTargets, setClientTargets] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openFoodLogId, setOpenFoodLogId] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  const messagesContainerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const loadMoreLockRef = useRef(false);
  const didInitialScrollRef = useRef(false);

  const applyTimelinePayload = useCallback((payload, { merge = false, updateHasMore = true } = {}) => {
    let nextEvents = normalizeTimelineEvents(payload);
    let truncated = false;
    if (!merge && nextEvents.length > TIMELINE_PAGE_SIZE) {
      nextEvents = nextEvents.slice(-TIMELINE_PAGE_SIZE);
      truncated = true;
    }
    const errors = sourceErrorsFromPayload(payload);
    setSourceErrors(errors);
    if (allSourcesFailed(errors, payload) && !merge) {
      setLoadError(he
        ? 'לא ניתן לטעון את ציר הזמן. בדקו את החיבור ונסו שוב.'
        : 'The activity timeline could not load. Check the connection and try again.');
      return nextEvents;
    }
    setLoadError(null);
    if (updateHasMore) {
      setHasMore(truncated || computeHasMore(payload));
    }
    setEvents((prev) => (merge ? mergeTimelineEvents(prev, nextEvents) : nextEvents));
    return nextEvents;
  }, [he]);

  const loadTimeline = useCallback(async () => {
    if (!selectedClient) {
      setEvents([]);
      setIsLoading(false);
      setLoadError(null);
      setClientTargets(null);
      return;
    }
    setIsLoading(true);
    isInitialLoadRef.current = true;
    didInitialScrollRef.current = false;
    try {
      const [payload, targetsPayload] = await Promise.all([
        fetchClientTimeline({
          clientId: selectedClient.id,
          userCode: selectedClient.user_code,
          limit: TIMELINE_PAGE_SIZE,
        }),
        fetchClientDailyTargets(selectedClient.id).catch(() => null),
      ]);
      applyTimelinePayload(payload, { merge: false });
      if (targetsPayload) {
        setClientTargets(targetsPayload);
      }
    } catch (err) {
      console.error('Error loading timeline:', err);
      setLoadError(err.message || (he
        ? 'לא ניתן לטעון את ציר הזמן. נסו שוב.'
        : 'The activity timeline could not load. Try again.'));
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedClient, applyTimelinePayload, he]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  // Lets the product tour close the food-log drawer it opened.
  useEffect(() => {
    const closeFoodDrawer = () => setOpenFoodLogId(null);
    window.addEventListener('portfolio-playbook-close-food-drawer', closeFoodDrawer);
    return () => window.removeEventListener('portfolio-playbook-close-food-drawer', closeFoodDrawer);
  }, []);

  useLayoutEffect(() => {
    if (isLoading || events.length === 0 || didInitialScrollRef.current) return undefined;
    const el = messagesContainerRef.current;
    if (!el) return undefined;
    const snapToLatest = () => {
      el.scrollTop = el.scrollHeight;
    };
    snapToLatest();
    const frame = window.requestAnimationFrame(snapToLatest);
    const timer = window.setTimeout(() => {
      snapToLatest();
      didInitialScrollRef.current = true;
      isInitialLoadRef.current = false;
    }, 60);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [isLoading, events.length]);

  useEffect(() => {
    if (!selectedClient) return undefined;
    const refresh = async () => {
      try {
        const payload = await fetchClientTimeline({
          clientId: selectedClient.id,
          userCode: selectedClient.user_code,
          limit: TIMELINE_PAGE_SIZE,
        });
        applyTimelinePayload(payload, { merge: true, updateHasMore: false });
      } catch (err) {
        console.error('Error refreshing timeline:', err);
      }
    };
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [selectedClient, applyTimelinePayload]);

  const handleLoadMore = useCallback(async () => {
    if (!didInitialScrollRef.current) return;
    if (!selectedClient || !hasMore || isLoadingMore || loadMoreLockRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const cursor = oldestCursor(events);
    if (!cursor) return;

    loadMoreLockRef.current = true;
    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const payload = await fetchClientTimeline({
        clientId: selectedClient.id,
        userCode: selectedClient.user_code,
        before: cursor,
        limit: TIMELINE_PAGE_SIZE,
      });
      const page = normalizeTimelineEvents(payload);
      if (!page.length) {
        setHasMore(false);
      } else {
        applyTimelinePayload(payload, { merge: true });
        window.setTimeout(() => {
          if (!messagesContainerRef.current) return;
          const heightDifference = messagesContainerRef.current.scrollHeight - prevScrollHeight;
          messagesContainerRef.current.scrollTop = prevScrollTop + heightDifference;
        }, 20);
      }
    } catch (err) {
      console.error('Error loading more timeline:', err);
      setLoadMoreError(err.message || (he
        ? 'לא ניתן לטעון פעילות ישנה יותר.'
        : 'Could not load older activity.'));
    } finally {
      setIsLoadingMore(false);
      loadMoreLockRef.current = false;
    }
  }, [selectedClient, hasMore, isLoadingMore, events, applyTimelinePayload, he]);

  useEffect(() => {
    const root = messagesContainerRef.current;
    const sentinel = topSentinelRef.current;
    if (!root || !sentinel || isLoading || !didInitialScrollRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!didInitialScrollRef.current || !entries[0]?.isIntersecting || loadMoreError) return;
        if (root.scrollHeight <= root.clientHeight + 8) return;
        if (root.scrollTop > 80) return;
        handleLoadMore();
      },
      { root, rootMargin: '80px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore, isLoading, events.length, loadMoreError]);

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const messageDate = new Date(dateString);
    const diffMs = now - messageDate;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return he ? 'עכשיו' : 'Just now';
    if (diffMins < 60) return he ? `לפני ${diffMins} דקות` : `${diffMins} min ago`;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    if (messageDay.getTime() === today.getTime()) return formatChatTime(dateString);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDay.getTime() === yesterday.getTime()) {
      const timeStr = formatChatTime(dateString);
      return he ? `אתמול ${timeStr}` : `Yesterday ${timeStr}`;
    }
    const dateStr = he
      ? messageDate.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })
      : messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${dateStr} ${formatChatTime(dateString)}`;
  };

  const formatChatDate = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    if (messageDay.getTime() === today.getTime()) return he ? 'היום' : 'Today';
    if (messageDay.getTime() === yesterday.getTime()) return he ? 'אתמול' : 'Yesterday';
    return messageDate.toLocaleDateString(he ? 'he-IL' : 'en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatChatTime = (date) => {
    return new Date(date).toLocaleTimeString(he ? 'he-IL' : 'en-US', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const shouldShowDateSeparator = (currentEvent, previousEvent) => {
    if (!previousEvent) return true;
    const c = new Date(currentEvent.timestamp);
    const p = new Date(previousEvent.timestamp);
    return c.setHours(0, 0, 0, 0) !== p.setHours(0, 0, 0, 0);
  };

  // Tour anchors: the newest day header and the newest food log card.
  let lastDateSeparatorIndex = -1;
  let newestFoodEventIndex = -1;
  events.forEach((event, index) => {
    if (shouldShowDateSeparator(event, events[index - 1])) lastDateSeparatorIndex = index;
    if (event.type === 'food') newestFoodEventIndex = index;
  });

  const handleImageClick = (imageSrc) => setSelectedImage(imageSrc);
  const handleCloseImageModal = () => setSelectedImage(null);

  useEffect(() => {
    const root = messagesContainerRef.current;
    if (!root) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = root;
      // When at the bottom, scrollTop + clientHeight is very close to scrollHeight
      if (scrollHeight - scrollTop - clientHeight > 150) {
        setShowScrollBottom(true);
      } else {
        setShowScrollBottom(false);
      }
    };
    root.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also attach a ResizeObserver to the container's children so it updates if images load
    const resizeObserver = new ResizeObserver(() => handleScroll());
    if (root.firstElementChild) {
      resizeObserver.observe(root.firstElementChild);
    }
    
    // Initial check
    handleScroll();
    return () => {
      root.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [events]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 50);
    }
  };

  const partialBanner = (() => {
    if (!anySourceError(sourceErrors) || loadError) return null;
    const parts = [];
    if (sourceErrors.messages) parts.push(he ? 'הודעות' : 'messages');
    if (sourceErrors.food_logs) parts.push(he ? 'יומן ארוחות' : 'food logs');
    if (sourceErrors.weight_logs) parts.push(he ? 'יומן משקל' : 'weight logs');
    return parts.length
      ? (he
        ? `חלק מהנתונים לא נטענו (${parts.join(', ')}).`
        : `Some activity could not load (${parts.join(', ')}).`)
      : null;
  })();

  if (!selectedClient) {
    return (
      <ClientWorkspaceLayout>
        <div />
      </ClientWorkspaceLayout>
    );
  }

  return (
    <ClientWorkspaceLayout fillViewport>
      <div
        className={`flex flex-col overflow-hidden relative min-h-0 flex-1 h-full
          ${isDarkMode ? 'bg-canvas-dark' : 'bg-canvas'}`}
        style={{ paddingBottom: 'var(--playbook-bottom-lift, 0px)' }}
        dir={direction}
      >
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {!isOnline && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-line dark:border-line-dark bg-moss dark:bg-moss-dark text-[13px] text-ink dark:text-ink-dark flex items-center gap-2" role="status">
          <WifiOff size={16} aria-hidden="true" />
          {he ? 'אין חיבור. ציר הזמן לא יתעדכן עד שתחזור הרשת.' : 'You are offline. The timeline will not update until you reconnect.'}
        </div>
      )}

      <div
        ref={messagesContainerRef}
        data-tour="playbook-chat-thread"
        className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 md:px-8 py-6 custom-scrollbar relative"
      >
        {partialBanner && !isLoading && (
          <div className="max-w-[1600px] mx-auto mb-4 rounded-lg border border-line dark:border-line-dark px-4 py-3 flex items-start sm:items-center justify-between gap-3" role="status">
            <p className="text-[13px] text-ink dark:text-ink-dark">{partialBanner}</p>
            <button type="button" onClick={loadTimeline} className={themeClasses.btnSecondary + ' !min-h-[44px] !px-3 !py-2 !text-[13px]'}>
              <RefreshCw size={14} />
              {he ? 'נסה שוב' : 'Retry'}
            </button>
          </div>
        )}

        {isLoading ? (
          <TimelineSkeleton />
        ) : loadError ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <p className="text-[15px] font-semibold text-ink dark:text-ink-dark mb-2">{he ? 'ציר הזמן לא נטען' : 'Timeline did not load'}</p>
            <p className="text-[13px] text-ink-muted dark:text-ink-muted-dark max-w-sm mb-4">{loadError}</p>
            <button type="button" onClick={loadTimeline} className={themeClasses.btnPrimary}>
              <RefreshCw size={16} />
              {he ? 'נסה שוב' : 'Try again'}
            </button>
          </div>
        ) : events.length === 0 ? (
          <div data-tour="playbook-chat-empty" className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className={`p-5 rounded-lg mb-6 ${isDarkMode ? 'bg-surface-dark border border-line-dark' : 'bg-surface border border-line'}`}>
              <MessageSquare size={40} className="text-ink-muted" strokeWidth={1.5} />
            </div>
            <h3 className="text-[18px] font-semibold mb-2 text-ink dark:text-ink-dark">
              {he ? 'אין פעילות עדיין' : 'No activity yet'}
            </h3>
            <p className="max-w-xs text-[13px] font-medium text-ink-muted dark:text-ink-muted-dark">
              {he
                ? 'כשהלקוח ישלח הודעה, יתעד ארוחה או ישקול, זה יופיע כאן לפי סדר הזמן.'
                : 'When this client messages, logs a meal, or logs a weight, it will appear here in time order.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6 w-full max-w-[1600px] mx-auto pb-4">
            <div ref={topSentinelRef} className="h-1 w-full" aria-hidden="true" />
            {loadMoreError && (
              <div className="flex justify-center py-2">
                <button type="button" onClick={handleLoadMore} className={themeClasses.btnSecondary + ' !text-[13px]'}>
                  {he ? 'טען פעילות ישנה יותר' : 'Load older activity'}
                </button>
              </div>
            )}
            {isLoadingMore && (
              <div className="flex justify-center py-2" aria-live="polite">
                <RefreshCw size={16} className="animate-spin text-forest-ondark" />
                <span className="sr-only">{he ? 'טוען היסטוריה' : 'Loading history'}</span>
              </div>
            )}

            {events.map((event, index) => (
              <div key={event.id}>
                {shouldShowDateSeparator(event, events[index - 1]) && (
                  <div className="flex justify-center my-6 relative">
                    <div
                      data-tour={index === lastDateSeparatorIndex ? 'playbook-chat-day-header' : undefined}
                      className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 px-4 py-1.5 rounded-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark shadow-sm z-10">
                      <span className="text-[12px] font-semibold text-ink dark:text-ink-dark">
                        {formatChatDate(event.timestamp)}
                      </span>
                      {(() => {
                        const eventDate = new Date(event.timestamp).setHours(0, 0, 0, 0);
                        const today = new Date().setHours(0, 0, 0, 0);
                        const isToday = eventDate === today;
                        const dayEvents = events.filter((e) => e.type === 'food' && new Date(e.timestamp).setHours(0, 0, 0, 0) === eventDate);
                        if (dayEvents.length === 0) return null;
                        
                        const dayCalories = Math.round(dayEvents.reduce((sum, e) => sum + (e.payload.total_calories || 0), 0));
                        const dayProtein = Math.round(dayEvents.reduce((sum, e) => sum + (e.payload.total_protein_g || 0), 0));
                        const dayCarbs = Math.round(dayEvents.reduce((sum, e) => sum + (e.payload.total_carbs_g || 0), 0));
                        const dayFat = Math.round(dayEvents.reduce((sum, e) => sum + (e.payload.total_fat_g || 0), 0));

                        const targetCals = clientTargets?.calories;
                        const isOver = targetCals && dayCalories > targetCals;

                        return (
                          <>
                            <span className="hidden sm:block w-px h-3 bg-line dark:bg-line-dark" aria-hidden="true"></span>
                            <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] tabular-nums font-medium">
                              <span className={`inline-flex items-center gap-1 ${isOver ? 'text-[color:var(--danger-text)]' : 'text-ink dark:text-ink-dark'}`}>
                                <Flame size={12} className={isOver ? 'text-[color:var(--danger-text)]' : 'text-ink-muted dark:text-ink-muted-dark'} aria-hidden="true" />
                                {dayCalories.toLocaleString()} {targetCals ? `/ ${targetCals.toLocaleString()}` : ''} {he ? 'קק״ל' : 'kcal'}
                              </span>
                              {(dayProtein > 0 || dayCarbs > 0 || dayFat > 0) && (
                                <div className="flex items-center gap-2">
                                  <span className="ind-protein">{dayProtein}g <span className="opacity-70 font-normal">P</span></span>
                                  <span className="ind-carbs">{dayCarbs}g <span className="opacity-70 font-normal">C</span></span>
                                  <span className="ind-fat">{dayFat}g <span className="opacity-70 font-normal">F</span></span>
                                </div>
                              )}
                            </div>
                            {isToday && (
                              <>
                                <span className="hidden sm:block w-px h-3 bg-line dark:bg-line-dark" aria-hidden="true"></span>
                                <span className="text-[11px] text-ink-muted dark:text-ink-muted-dark italic">
                                  {he ? 'עד כה' : 'so far'}
                                </span>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {event.type === 'chat' && (
                  <ChatBubble
                    event={event}
                    isDarkMode={isDarkMode}
                    onImageClick={handleImageClick}
                    timeLabel={formatMessageTime(event.payload.created_at || event.timestamp)}
                  />
                )}
                {event.type === 'food' && (
                  <FoodLogCard
                    event={event}
                    language={language}
                    timeLabel={formatChatTime(event.timestamp)}
                    onOpen={setOpenFoodLogId}
                    onImageClick={handleImageClick}
                    tourId={index === newestFoodEventIndex ? 'playbook-chat-food-card' : undefined}
                  />
                )}
                {event.type === 'weight' && (() => {
                  // events are oldest -> newest, so the previous weigh-in is the nearest earlier one.
                  const olderWeightEvents = events.slice(0, index).filter(e => e.type === 'weight');
                  const previousWeight = olderWeightEvents.length > 0 ? olderWeightEvents[olderWeightEvents.length - 1].payload.weight_kg : clientTargets?.initialWeightKg;
                  return (
                    <WeightLogCard
                      event={event}
                      language={language}
                      timeLabel={formatChatTime(event.timestamp)}
                      clientTargets={clientTargets}
                      previousWeight={previousWeight}
                    />
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {showScrollBottom && (
          <div className={`fixed bottom-24 ${direction === 'rtl' ? 'left-6' : 'right-6'} z-[90]`}>
            <button
              type="button"
              onClick={scrollToBottom}
              className="bg-forest-ondark hover:bg-forest text-white dark:text-canvas-dark p-3 rounded-full shadow-lg transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-forest"
              aria-label={he ? 'גלול למטה' : 'Scroll to bottom'}
            >
              <ChevronDown size={24} />
            </button>
          </div>
        )}
      </div>

      <div className={`flex-shrink-0 z-20 px-4 sm:px-6 lg:px-8 py-4 border-t ${isDarkMode ? 'bg-canvas-dark border-line-dark' : 'bg-canvas border-line'}`}>
        <div className="w-full max-w-[1600px] mx-auto text-center">
          <p className="text-[13px] font-medium text-ink-muted dark:text-ink-muted-dark">
            {he ? 'תצוגה זו הינה לקריאה בלבד.' : 'This view is read-only.'}
          </p>
        </div>
      </div>

      {openFoodLogId && (() => {
        const logEvent = events.find((e) => e.type === 'food' && e.payload.id === openFoodLogId);
        let todayCaloriesSoFar = 0;
        if (logEvent) {
          const eventDate = new Date(logEvent.timestamp).setHours(0, 0, 0, 0);
          const dayEvents = events.filter((e) => e.type === 'food' && new Date(e.timestamp).setHours(0, 0, 0, 0) === eventDate);
          todayCaloriesSoFar = dayEvents.reduce((sum, e) => sum + (e.payload.total_calories || 0), 0);
        }
        return (
          <FoodLogDrawer
            logId={openFoodLogId}
            language={language}
            onClose={() => setOpenFoodLogId(null)}
            onImageClick={handleImageClick}
            clientTargets={clientTargets}
            todayCaloriesSoFar={todayCaloriesSoFar}
          />
        );
      })()}

      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-canvas-dark/90"
          onClick={handleCloseImageModal}
          role="dialog"
          aria-modal="true"
          aria-label={he ? 'תמונה מוגדלת' : 'Expanded image'}
        >
          <img src={selectedImage} alt="" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            onClick={handleCloseImageModal}
            className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center text-white bg-black/40 rounded-full hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={he ? 'סגור' : 'Close'}
          >
            <X size={28} />
          </button>
        </div>
      )}
    </div>
  </ClientWorkspaceLayout>
  );
}

export default ChatPage;
